<template>
  <div
    v-if="settings"
    class="bg-center bg-cover h-[40rem] flex flex-col justify-center items-center p-2 md:-px-0"
    :style="{ 'background-image': `url(${settings.banner})` }"
  >
    <div
      v-if="settings"
      class="w-full px-4 py-6 bg-white rounded-lg shadow-md md:max-w-5xl md:p-8"
    >
      <p
        v-if="message"
        class="text-sm font-semibold text-center text-secondary"
      >
        {{ message }}
      </p>
      <!-- Tabs for Buy and Rent -->
      <div class="flex space-x-2">
        <button
          :class="{
            'bg-blue-100 border border-[#6495ce]':
              propertyTenure === rentOption.id,
            'bg-neutral-100': propertyTenure !== rentOption.id,
          }"
          @click="setPropertyTenure(rentOption.id, 'properties-for-rent')"
          class="px-4 py-2 text-sm font-normal text-gray-600 rounded"
          v-if="rentOption"
        >
          Rent
        </button>

        <button
          :class="{
            'bg-blue-100 border border-[#6495ce]':
              propertyTenure === buyOption.id,
            'bg-neutral-100': propertyTenure !== buyOption.id,
          }"
          @click="setPropertyTenure(buyOption.id, 'properties-for-sale')"
          class="px-4 py-2 text-sm font-normal text-gray-600 rounded"
          v-if="buyOption"
        >
          Buy
        </button>
        <div
          class="inline-block min-h-[1em] w-0.5 self-stretch bg-neutral-200 opacity-100 dark:opacity-50"
        ></div>
        <div class="flex items-center space-x-2">
          <input
            type="checkbox"
            id="showCommercial"
            class="border-gray-300 rounded accent-primary"
            value="commercial"
            v-model="property_tenure"
          />
          <label
            for="showCommercial"
            class="text-sm font-normal text-gray-600"
            >{{
              commercialOption
                ? commercialOption.name
                : "Show commercial properties only"
            }}</label
          >
        </div>
      </div>

      <div
        class="w-full mx-auto mt-4 space-y-3 md:flex md:justify-between md:space-x-3 md:space-y-0"
      >
        <div class="md:w-[520px]">
          <input
            type="text"
            class="w-full h-[48px] px-4 py-2 border-[#ccc] rounded-md placeholder:tracking-wide placeholder:text-gray-500 text-gray-500 focus:outline outline-primary outline-1 border placeholder:font-light"
            v-model="keyword"
            placeholder="City, Community, or Building"
          />
        </div>

        <!-- property type -->
        <div class="md:w-auto" v-if="propertyTypes">
          <DropDown
            height="[48px]"
            buttonLabel="Property Type"
            :menuItems="propertyTypes"
            radius="md"
            @menuItemSelected="handleMenuItemSelected"
          />
        </div>

        <!-- beds & bath -->
        <div class="md:w-auto">
          <dropdownBedBath
            height="[48px]"
            buttonLabel="Bed & Bath"
            radius="md"
            @bathBedSelected="handleBathBedSelected"
          />
        </div>

        <!-- Price -->
        <div class="md:w-auto">
          <PriceDropdown
            height="[48px]"
            buttonLabel="Price"
            radius="md"
            @priceRangeSelected="handlePriceRangeSelected"
          />
        </div>

        <button
          @click="handleSearch"
          class="flex items-center justify-center w-full md:w-14 h-[48px] px-2 py-1 text-white bg-primary rounded hover:bg-[#910811]"
        >
          <SearchSvg color="white" width="4" height="4" stroke="#ffff" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";

import SearchSvg from "@/components/svg/SearchSvg.vue";
import DropDown from "@/components/form-components/DropDown.vue";
import dropdownBedBath from "@/components/form-components/BedBathDropdown.vue";
import PriceDropdown from "@/components/form-components/PriceDropdown.vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import axios from "axios";

export default {
  name: "BannerComponent",
  components: {
    DropDown,
    dropdownBedBath,
    PriceDropdown,
    SearchSvg,
  },
  setup() {
    const store = useStore();
    const base_url = store.state.baseUrl;
    const router = useRouter();
    const activeTab = ref("buy");
    const isModalOpen = ref(false);
    const property_tenure = ref(null);
    const keyword = ref("");
    const minPrice = ref("");
    const maxPrice = ref("");
    const numBed = ref("");
    const numBath = ref("");
    const property_type_id = ref("");
    const propertyTenureOptions = ref([]);
    const rentOption = ref(null);
    const buyOption = ref(null);
    const propertyTenure = ref(null);
    const propertyTypes = ref(null);
    const slug = ref(null);
    let message = ref(null);
    let settings = ref("");

    const loadSettings = async () => {
      await store.dispatch("fetchSettings");
      settings.value = store.state.settings.data;
    };
    onMounted(() => {
      loadSettings();
    });

    const fetchPropertyTenureOptions = async () => {
      try {
        const response = await axios.get(`${base_url}property-tenure-options`);
        propertyTenureOptions.value = response.data.data;

        rentOption.value = propertyTenureOptions.value.find(
          (option) => option.name.toLowerCase() === "rent"
        );
        buyOption.value = propertyTenureOptions.value.find(
          (option) => option.name.toLowerCase() === "buy"
        );
      } catch (error) {
        console.error("Error fetching property tenure options:", error);
      }
    };

    const fetchPropertyTypes = async () => {
      try {
        const response = await axios.get(`${base_url}property-types`);
        propertyTypes.value = response.data.data;
      } catch (error) {
        console.error("Error fetching property tenure options:", error);
      }
    };

    const setPropertyTenure = (id, slugValue) => {
      propertyTenure.value = id;
      slug.value = slugValue;
    };

    // Define methods
    const setActiveTab = (tab) => {
      activeTab.value = tab;
      property_tenure.value = tab;
    };

    const openModal = () => {
      isModalOpen.value = true;
    };

    const closeModal = () => {
      isModalOpen.value = false;
    };

    const handleBathBedSelected = (payload) => {
      numBed.value = payload.Bedroom.value;
      numBath.value = payload.Bathroom.value;
    };

    const handlePriceRangeSelected = (payload) => {
      minPrice.value = payload.minPrice;
      maxPrice.value = payload.maxPrice;
    };

    const handleMenuItemSelected = (payload) => {
      property_type_id.value = payload.itemId;
    };

    const handleSearch = () => {
      if (slug.value == null) {
        message.value =
          "Select a property tenure option, buy, rent or commercial";

        setTimeout(() => {
          message.value = null;
        }, 4000);
      } else {
        const queryParams = {
          keyword: keyword.value || "",
          property_tenure_option_id: propertyTenure.value || "",
          property_type_id: property_type_id.value || "",
          number_of_beds: numBed.value || "",
          number_of_baths: numBath.value || "",
          min_price: minPrice.value || "",
          max_price: maxPrice.value || "",
        };

        router.push({
          name: "properties",
          params: { slug: slug.value },
          query: queryParams,
        });
      }
    };

    onMounted(() => {
      fetchPropertyTenureOptions();
      fetchPropertyTypes();
    });

    const commercialOption = computed(() => {
      return propertyTenureOptions.value.find((option) =>
        option.name.toLowerCase().includes("commercial")
      );
    });

    return {
      settings,
      propertyTenure,
      propertyTypes,
      slug,
      message,
      rentOption,
      buyOption,
      setPropertyTenure,
      commercialOption,
      activeTab,
      isModalOpen,
      setActiveTab,
      openModal,
      closeModal,
      property_tenure,
      property_type_id,
      keyword,
      minPrice,
      maxPrice,
      numBed,
      numBath,
      handleBathBedSelected,
      handlePriceRangeSelected,
      handleMenuItemSelected,
      handleSearch,
      propertyTenureOptions,
    };
  },
};
</script>

<style scoped>
.custom-select {
  position: relative;
  display: inline-block;
}

.custom-select select {
  padding: 10px; /* Adjust the padding value as needed */
  padding-right: 30px; /* Adjust the right padding to create space for the dropdown icon */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.custom-select::after {
  content: "\2304"; /* Unicode character for the dropdown arrow */
  font-size: 16px; /* Adjust the font size of the dropdown arrow */
  color: #9ba3af; /* Adjust the color of the dropdown arrow */
  position: absolute;
  top: 50%;
  right: 10px; /* Adjust the right position of the dropdown arrow */
  transform: translateY(-50%);
}
</style>
